import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { Subject } from 'rxjs';
import { NotificationService, User, UserService } from '@beautyapp/core';
import { NZ_DRAWER_DATA } from 'ng-zorro-antd/drawer';
import { NzModalService } from 'ng-zorro-antd/modal';
import { takeUntil } from 'rxjs/operators';
import { UserAddEditComponent, UserVerifyComponent } from '@beautyapp/shared';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzSpinComponent } from 'ng-zorro-antd/spin';
import { UserTransactionsComponent } from '../user-transactions/user-transactions.component';
import { UserEhrComponent } from '../user-ehr/user-ehr.component';
import { UserBookingHistoryComponent } from '../user-booking-history/user-booking-history.component';
import { UserPackagesComponent } from '../user-packages/user-packages.component';
import { AuthState } from '@beautyapp/auth';
import {
  Customer360CommunicationsComponent,
  Customer360ContentComponent,
} from '@beautyapp/customer-360';
import { UserOverviewPanelComponent } from '../user-overview/user-overview-panel.component';
import { UserConsignationsComponent } from '../user-consignations/user-consignations.component';
import { UserConsentHistoryComponent } from '../user-consent-history/user-consent-history.component';

/**
 * beaut-497. Position of the service-packages tab in the template, counting
 * from zero: information, visits, transactions, consignation, SERVICE PACKAGES.
 *
 * ng-zorro selects a tab by index and offers no way to name one, so this is a
 * positional reference into markup nobody promised to keep still. Insert a tab
 * above it and this must move with it — `user-view.component.spec.ts` reads the
 * template and checks this number against it, so that mistake fails a test
 * instead of quietly opening the wrong tab.
 *
 * Exported (rather than a plain class field) so that test can read the value
 * without constructing a component that wants a drawer, a store and HTTP.
 */
export const SERVICE_PACKAGES_TAB_INDEX = 4;

@Component({
  selector: 'beautyapp-user-view',
  templateUrl: './user-view.component.html',
  styleUrls: ['./user-view.component.scss'],
  imports: [
    NzTabsModule,
    TranslateModule,
    NzSpinComponent,
    UserTransactionsComponent,
    UserEhrComponent,
    UserBookingHistoryComponent,
    UserPackagesComponent,
    Customer360ContentComponent,
    Customer360CommunicationsComponent,
    UserOverviewPanelComponent,
    UserConsignationsComponent,
    UserConsentHistoryComponent
  ]
})
export class UserViewComponent implements OnInit, OnDestroy {

  private service: UserService = inject(UserService);
  private notificationService: NotificationService = inject(NotificationService);
  private modalService: NzModalService = inject(NzModalService);
  private translateService: TranslateService = inject(TranslateService);
  private store: Store = inject(Store);

  data = inject(NZ_DRAWER_DATA).data;

  userData = signal<User | null>(null);

  sub$: Subject<any> = new Subject<any>();

  activeBranch = this.store.selectSignal((state) => state.auth.activeBranch);
  organization = this.store.selectSignal(AuthState.getOrganization);
  ehrActive = computed(() => this.activeBranch().ehrEnabled && this.organization()?.clinicId);

  /**
   * beaut-497. Which tab is open. Two-way bound so the gift indicator on the
   * overview panel can jump here.
   */
  selectedTab = 0;

  /** Template alias for {@link SERVICE_PACKAGES_TAB_INDEX}. */
  readonly SERVICE_PACKAGES_TAB = SERVICE_PACKAGES_TAB_INDEX;

  /**
   * beaut-497. Opens the service packages tab. It used to switch that tab's
   * gift filter on as well; it no longer does. That tab is for service
   * packages, and dropping the operator into a gifts-only view hides the rest
   * of what they came for. Gifts are still picked out there by a row tag.
   */
  showGiftPackages(): void {
    this.selectedTab = this.SERVICE_PACKAGES_TAB;
  }

  ngOnInit() {
    this.getEmployee(this.data);
  }

  ngOnDestroy(): void {
    this.sub$.next(null);
    this.sub$.complete();
  }

  getEmployee(id: any) {
    this.service.getById(id)
      .pipe(takeUntil(this.sub$))
      .subscribe(res => {
        this.userData.set(res);
      });
  }

  addOrEdit(event?: any) {
    this.modalService.create({
      nzTitle: this.translateService.instant('users.addUser/edit'),
      nzContent: UserAddEditComponent,
      nzWidth: '800px',
      nzData: {
        user: event
      },
      nzOnOk: () => {
        this.getEmployee(this.data);
      }
    });
  }

  userVerify(user: User) {
    if (user.isVerified) {
      return;
    }

    this.modalService.create({
      nzTitle: this.translateService.instant('users.verifyUser'),
      nzContent: UserVerifyComponent,
      nzWidth: '400px',
      nzData: {
        userId: user.id
      },
      nzOnOk: () => {
        this.getEmployee(this.data);
      }
    });
  }
}
